import { sha256 } from '../lib/createHash'
import watch from 'redux-watch'

import {
  transactionStart,
  transactionEvent,
  transactionComplete,
  transactionFailure,
  transactionUpdate,
} from '../actions/AnalyticsActions'
import { AppStore } from 'src/client/store'
import { BuyerType } from 'reducers/analytics'
import { reportError } from '../services/errorReportingService'
import { getOrderWithPaymentsAndReservations } from 'api/order'
import { fireInteractionEvent, fireNonInteractionEvent } from 'api/googleTagManager'
import { insurancePurchased } from 'analytics/eventDefinitions'
import { trackPageView } from '@snowplow/browser-tracker'
import { PAGE_VIEW_CONTEXT_SCHEMA } from 'constants/analytics'
import config from 'constants/config'
import { isStandaloneFlightsOrder } from 'lib/orderUtils'

export function watchAnalytics(store: AppStore, dataLayer: any) {
  setDataLayerDetails(store, dataLayer)

  store.subscribe(
    watch(store.getState, 'analytics.page.currentPath')(() => {
      // make sure that the first page view is correct logged with userId (if exists)
      if (store.getState().auth.initComplete) {
        const page = store.getState().analytics.page
        if (page.currentPath === page.prevPath) {
          return
        }

        trackPageView({
          context: [{
            schema: PAGE_VIEW_CONTEXT_SCHEMA,
            data: {
              pathTemplate: page.currentPathTemplate,
              pathParams: JSON.stringify(page.currentPathParams),
            },
          }],
        })
      }
    }),
  )

  store.subscribe(
    watch(store.getState, 'auth.initComplete')(() => {
      const page = store.getState().analytics.page
      trackPageView({
        context: [{
          schema: PAGE_VIEW_CONTEXT_SCHEMA,
          data: {
            pathTemplate: page.currentPathTemplate,
            pathParams: JSON.stringify(page.currentPathParams),
          },
        }],
      })
    }),
  )

  store.subscribe(
    watch(store.getState, 'analytics.transaction')(() => {
      try {
        const { analytics, auth, geo, config: stateConfig } = store.getState()
        const {
          cartSuccess,
          orderSuccess,
          eventComplete,
          cart,
          order,
          isNewOrder,
        } = analytics.transaction

        const orderIdValid = order.id === cart.order?.id
        const orderComplete = cartSuccess && orderSuccess && orderIdValid
        if (orderComplete) {
          // Avoid duplicate tracking of a single event
          if (eventComplete) {
            return
          }

          store.dispatch(transactionEvent({
            auth,
            geo,
            isNewOrder,
            ...analytics.transaction,
          }))

          if (order.insuranceItems?.length > 0) {
            fireInteractionEvent(insurancePurchased())
          }

          // Temporary event to better track standalone flights
          if (!!stateConfig.experiments[config.AB_TEST_UNIVERSAL_CHECKOUT_STANDALONE_FLIGHTS_ID] && isStandaloneFlightsOrder(order)) {
            fireNonInteractionEvent({
              category: 'Standalone Flights',
              action: 'Purchase',
              label: 'Control',
            })
          }
          // End of temp event
        }
      } catch (e) {
        // don't bubble up to app. we don't want it failing due to this
        // but we do want to report it to sentry so we be aware of it
        reportError(e, undefined, store.getState())
        if (process.env.NODE_ENV === 'development') {
          console.error('Error during analytics event', e)
        }
      }
    }),
  )
  store.subscribe(
    watch(store.getState, 'cart')(() => {
      const { cart } = store.getState()

      // bad data was causing analytics actions to throw errors
      // and crash the app. So wrap in a try/catch and ignore errors
      try {
        if (cart.processing) {
          store.dispatch(transactionStart(cart))
        }
        if (cart.processed) {
          store.dispatch(transactionUpdate(cart))
          getTransactionOrder(store, cart.order.id)
        }
      } catch (e) {
        // don't bubble up to app. we don't want it failing due to this
        reportError(e, undefined, store.getState())
        if (process.env.NODE_ENV === 'development') {
          console.error('Error during analytics event', e)
        }
      }
    }),
  )
}

function getTransactionOrder(store: AppStore, orderId: string) {
  return getOrderWithPaymentsAndReservations(orderId)
    .then(order => store.dispatch(transactionComplete(order)))
    .catch(error => store.dispatch(transactionFailure(error)))
}

function setDataLayerDetails(store: AppStore, dataLayer: any) {
  const { auth, geo, utm, config } = store.getState() as App.State
  const details = {
    hitType: 'dataLayer-initialized',
    event: 'dataLayer-initialized',
    pageDetails: {
      siteVersion: '2.0',
    },
    sessionDetails: {
      loggedIn: auth.account.memberId ? 'loggedIN' : 'loggedOUT',
      selectedLocation: geo.currentRegionCode,
      utm,
      headlessMode: config.headlessMode,
      startLoggedIn: !!auth.account.memberId,
    },
    userDetails: {
      userID: auth.account.memberId || '',
      userEmailRaw: auth.account.email || '',
      userEmail: sha256(auth.account.email) || '',
      personContactID: auth.account.personContactId || '',
      buyerType: auth.account.total_purchase_count > 0 ? BuyerType.existing : BuyerType.new,
      utmSource: auth.account.utmSource || '',
      utmMedium: auth.account.utmMedium || '',
      utmCampaign: auth.account.utmCampaign || '',
      utmContent: auth.account.utmContent || '',
      utmTerm: auth.account.utmTerm || '',
      utmAdgroup: auth.account.utmAdgroup || '',
    },
  }
  dataLayer.push(details)
}
